import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { CurrentUser } from '../api/types/auth';
import { registeredModules } from '../app/modules';
import type { ModuleMenuItem } from '../app/moduleRegistry';
import { useBreadcrumbLeaf } from './breadcrumbContext';
import { useAuthStore } from '../auth/authStore';

export interface Crumb {
  label: string;
  /** Tıklanabilir crumb'lar için hedef; grup adı gibi ara crumb'larda yok. */
  to?: string;
}

/** Pathname'e en iyi uyan menü öğesi — en uzun `/${path}` öneki kazanır. */
function matchMenuItem(pathname: string, items: ModuleMenuItem[]): ModuleMenuItem | undefined {
  let best: ModuleMenuItem | undefined;
  for (const item of items) {
    const full = `/${item.path}`;
    if (pathname === full || pathname.startsWith(`${full}/`)) {
      if (!best || item.path.length > best.path.length) best = item;
    }
  }
  return best;
}

export function computeBreadcrumbs(
  pathname: string,
  currentUser: CurrentUser | null,
  leaf: string | null
): Crumb[] {
  const home: Crumb = { label: 'Anasayfa', to: '/' };
  if (pathname === '/' || pathname === '') return [home];

  const items = registeredModules
    .flatMap((m) => m.menuItems)
    .filter((item) => item.isVisible?.(currentUser) ?? true);

  const matched = matchMenuItem(pathname, items);
  if (!matched) return [home];

  const crumbs: Crumb[] = [home];
  if (matched.group) crumbs.push({ label: matched.group });
  crumbs.push({ label: matched.label, to: `/${matched.path}` });

  // Menü öğesinin path'inden daha derindeyiz → bir yaprak crumb ekle.
  const isDeeper = pathname.startsWith(`/${matched.path}/`);
  if (isDeeper) crumbs.push({ label: leaf ?? 'Detay' });

  return crumbs;
}

export function useBreadcrumbs(): Crumb[] {
  const location = useLocation();
  const currentUser = useAuthStore((s) => s.currentUser);
  const leaf = useBreadcrumbLeaf();
  return useMemo(
    () => computeBreadcrumbs(location.pathname, currentUser, leaf),
    [location.pathname, currentUser, leaf]
  );
}
